import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { MdbPopconfirmRef, MdbPopconfirmService } from 'mdb-angular-ui-kit/popconfirm';
import { finalize } from 'rxjs';
import {
  MESSAGE_CONSTANTS,
  NIGHTWEB_APP_CONSTANT,
  NODATA_MESSAGES,
} from 'src/app/constants';
import { WatchTheme, WatchThemeStatus } from 'src/app/models';
import { ToasterService, WatchThemeService } from 'src/app/services';
import { NightwebUtil } from 'src/app/utils';
import { PopConfirmComponent } from '../common';
import { AddEditWatchThemeModalComponent } from '../modals';
import { WatchThemeBriefModalComponent } from '../modals';

@Component({
  selector: 'app-watch-theme',
  templateUrl: './watch-theme.component.html',
  styleUrls: ['./watch-theme.component.scss'],
  standalone: false,
})
export class WatchThemeComponent implements OnInit {
  watchThemeForm!: FormGroup;
  loading = false;
  submitted = false;
  themes: WatchTheme[] = [];
  totalDataCount = 0;
  notFoundMessage = NODATA_MESSAGES.NO_WATCH_THEMES;
  limits = NIGHTWEB_APP_CONSTANT.showLimits;
  defaultLimitSelectWidth = NIGHTWEB_APP_CONSTANT.defaultLimitSelectWidth;
  statusFilterOptions = [
    { label: 'All statuses', value: 'All' },
    { label: WatchThemeStatus.AVAILABLE, value: WatchThemeStatus.AVAILABLE },
    { label: WatchThemeStatus.PENDING_BUILD, value: WatchThemeStatus.PENDING_BUILD },
    { label: WatchThemeStatus.RETIRED, value: WatchThemeStatus.RETIRED },
  ];
  config = NIGHTWEB_APP_CONSTANT.modalConfig;
  confirmPopConfig = NIGHTWEB_APP_CONSTANT.confirmPopConfig;
  addEditModalRef!: MdbModalRef<AddEditWatchThemeModalComponent>;
  briefModalRef!: MdbModalRef<WatchThemeBriefModalComponent>;
  popconfirmRef: MdbPopconfirmRef<PopConfirmComponent> | null = null;
  watchThemeStatus = WatchThemeStatus;
  defaultDateFormat = NIGHTWEB_APP_CONSTANT.defaultDateFormat;

  @ViewChild('tableElement') tableElement!: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private nightWebUtil: NightwebUtil,
    private modalService: MdbModalService,
    private toasterService: ToasterService,
    private popconfirmService: MdbPopconfirmService,
    private watchThemeService: WatchThemeService,
  ) {
    this.setForm();
  }

  ngOnInit(): void {
    this.getThemes();
  }

  setForm() {
    this.watchThemeForm = this.formBuilder.group({
      keyword: [''],
      status: ['All'],
      limit: [NIGHTWEB_APP_CONSTANT.defaultLimit],
      offset: [NIGHTWEB_APP_CONSTANT.defaultOffset],
    });
  }

  getThemes() {
    const filter: Record<string, any> = this.nightWebUtil.getFilterParams(this.watchThemeForm.value);
    if (filter['status'] === 'All') {
      delete filter['status'];
    }
    this.loading = this.watchThemeForm.value.offset === NIGHTWEB_APP_CONSTANT.defaultOffset;
    if (this.watchThemeForm.value.offset === NIGHTWEB_APP_CONSTANT.defaultOffset) {
      this.themes = [];
    }
    this.watchThemeService
      .getAll(filter)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.totalDataCount = response.data.count;
          const rows = response.data.rows || [];
          this.themes =
            this.watchThemeForm.value.offset !== NIGHTWEB_APP_CONSTANT.defaultOffset
              ? [...this.themes, ...rows]
              : rows;
        },
        error: () => {},
      });
  }

  statusBadgeClass(theme: WatchTheme): string {
    if (theme.status === WatchThemeStatus.AVAILABLE) {
      return 'badge rounded-pill bg-opacity-25 bg-success text-success';
    }
    if (theme.status === WatchThemeStatus.PENDING_BUILD) {
      return 'badge rounded-pill bg-opacity-25 bg-warning text-dark';
    }
    return 'badge rounded-pill bg-gray-light text-gray';
  }

  openAddEditModal(theme?: WatchTheme) {
    const config = this.nightWebUtil.shallowCopy(this.config);
    config.data.title = theme ? 'Edit Watch Theme' : 'Add Watch Theme';
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.formData = theme ? { ...theme, mode: 'edit' } : { mode: 'add' };
    this.addEditModalRef = this.modalService.open(AddEditWatchThemeModalComponent, config);
    this.addEditModalRef.onClose.subscribe((data: any) => {
      if (data) {
        this.watchThemeForm.controls['offset'].setValue(NIGHTWEB_APP_CONSTANT.defaultOffset);
        this.getThemes();
      }
    });
  }

  openBrief(theme: WatchTheme, event?: Event) {
    event?.stopPropagation();
    if (!theme.designBrief) {
      return;
    }
    const config = this.nightWebUtil.shallowCopy(this.config);
    config.data.title = theme.designBrief.fileName;
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.formData = { themeId: theme.id, brief: theme.designBrief };
    this.briefModalRef = this.modalService.open(WatchThemeBriefModalComponent, config);
  }

  confirmDelete(event: Event, theme: WatchTheme) {
    const target = event.target as HTMLElement;
    const config = this.nightWebUtil.shallowCopy(this.confirmPopConfig);

    if (theme.isDefault) {
      config.data.title = 'Cannot delete theme';
      config.data.body =
        `${theme.name} is the default fallback. Promote another Available theme to default ` +
        `before deleting or retiring this one.`;
      config.data.confirmButtonLabel = 'Okay';
      config.data.confirmButtonClass = 'btn-primary';
      config.data.showCancelButton = false;
      this.popconfirmRef = this.popconfirmService.open(PopConfirmComponent, target, config);
      return;
    }

    if (theme.organizationCount > 0) {
      config.data.title = 'Cannot delete theme';
      config.data.body =
        `${theme.organizationCount} organizations are still using ${theme.name}. Move them to ` +
        `another Available theme, or set this theme to Retired (reassigns to the default face ` +
        `on the next check-in).`;
      config.data.cancelButtonLabel = 'Close';
      config.data.confirmButtonLabel = 'Set to Retired';
      this.popconfirmRef = this.popconfirmService.open(PopConfirmComponent, target, config);
      this.popconfirmRef.onConfirm.subscribe(() => {
        this.watchThemeService.retire(theme.id).subscribe({
          next: () => {
            this.toasterService.show('Success', MESSAGE_CONSTANTS.WATCH_THEME_SAVED);
            this.reloadThemes();
          },
          error: () => this.reloadThemes(),
        });
      });
      return;
    }

    config.data.title = 'Delete Watch Theme';
    config.data.body = `Delete ${theme.name}? No organization is using it, so nothing on any watch changes. This cannot be undone.`;
    config.data.confirmButtonLabel = 'Delete';
    this.popconfirmRef = this.popconfirmService.open(PopConfirmComponent, target, config);
    this.popconfirmRef.onConfirm.subscribe(() => {
      this.watchThemeService.delete(theme.id).subscribe({
        next: () => {
          this.toasterService.show('Success', MESSAGE_CONSTANTS.WATCH_THEME_DELETED);
          this.reloadThemes();
        },
        error: () => this.reloadThemes(),
      });
    });
  }

  private reloadThemes() {
    this.watchThemeForm.controls['offset'].setValue(NIGHTWEB_APP_CONSTANT.defaultOffset);
    this.getThemes();
  }

  receiveSelectedValue(event: any) {
    if (!event) {
      return;
    }
    this.watchThemeForm.patchValue({
      [event.controlName]: event.value,
      offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
    });
    this.getThemes();
  }

  receiveSearchKeyword(keyword: any) {
    this.watchThemeForm.patchValue({
      keyword,
      offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
    });
    this.getThemes();
  }

  onScroll(event: Event) {
    const nativeElement = event.target as HTMLElement;
    if (!nativeElement?.scrollTop) {
      return;
    }
    const height = Math.round(nativeElement.clientHeight + nativeElement.scrollTop + 1);
    if (height >= nativeElement.scrollHeight && this.themes.length !== this.totalDataCount) {
      this.watchThemeForm.controls['offset'].setValue(this.watchThemeForm.value.offset + 1);
      this.getThemes();
    }
  }
}
